<template>
  <!-- Run New Analysis (ticket 07, approved showcase ai-analysis-run-dialog-showcase.html, mode choice variant A). -->
  <v-dialog :value="value" :max-width="width" scrollable @input="$emit('input', $event)" @keydown.esc="close">
    <section class="run-dialog" :class="{ 'is-processing': !!blockedReason }" role="dialog" aria-labelledby="runDialogTitle">
      <header class="run-dialog__head">
        <div class="run-dialog__head-text">
          <h2 class="run-dialog__title" id="runDialogTitle">Run New Analysis</h2>
          <p class="run-dialog__sub">{{ facilityName || 'This facility' }}</p>
        </div>
        <v-btn icon small class="run-dialog__close" aria-label="Close dialog" @click="close"><v-icon :size="20">mdi-close</v-icon></v-btn>
      </header>

      <div v-if="blockedReason || error" class="run-dialog__alerts">
        <div v-if="blockedReason" class="alert alert--warn" role="status">
          <v-icon :size="18" class="alert__icon">mdi-alert</v-icon>
          <span class="alert__body"><strong>{{ copy.PROCESSING_TITLE }}</strong>{{ copy.PROCESSING_BODY }}</span>
        </div>
        <div v-else class="alert alert--error" role="alert">
          <v-icon :size="18" class="alert__icon">mdi-alert</v-icon>
          <span class="alert__body"><strong>{{ copy.ERROR_TITLE }}</strong>{{ error }}</span>
          <v-btn small text color="error" class="alert__action" @click="$emit('retry')">Try again</v-btn>
        </div>
      </div>

      <div class="run-dialog__body">
        <div class="field">
          <span class="field-label" id="runModeLabel">What do you want to analyse?</span>
          <div class="seg" role="radiogroup" aria-labelledby="runModeLabel">
            <button v-for="option in modes" :key="option.value" type="button" class="seg-btn" role="radio" :aria-checked="String(mode === option.value)" @click="mode = option.value">
              <v-icon :size="16" class="seg-btn__icon">{{ option.icon }}</v-icon>
              {{ option.label }}
            </button>
          </div>
          <p class="helper">{{ isFullScan ? copy.SEG_HELPER_FULL_SCAN : copy.SEG_HELPER_AVA_INSPECT }}</p>
        </div>

        <!-- Full Scan path -->
        <div v-if="isFullScan" class="panel">
          <div class="field">
            <span class="field-label" id="runScanLabel">Scan</span>
            <label class="tf"><v-icon :size="18" class="tf__icon">mdi-magnify</v-icon><input v-model="scanQuery" type="text" placeholder="Search scans by name…" aria-label="Search scans" /></label>
            <div class="list" role="radiogroup" aria-labelledby="runScanLabel">
              <button v-for="scan in visibleScans" :key="scan.id" type="button" class="list-row" role="radio" :aria-checked="String(selectedScanIds.includes(scan.id))" @click="toggleScan(scan.id)">
                <span class="radio-dot"></span>
                <span class="list-main">
                  <span class="list-name">{{ scan.name }}</span>
                  <span class="list-meta"><span v-if="scan.captured">Captured {{ scan.captured }}</span><span v-if="scan.note" class="chip chip--flat">{{ scan.note }}</span></span>
                </span>
              </button>
              <p v-if="!visibleScans.length" class="list-empty">{{ scans.length ? `No scan matches “${scanQuery}”.` : 'No 360 scans in this facility yet.' }}</p>
            </div>
          </div>
          <div v-if="selectedScans.length" class="field">
            <span class="field-label">Selected</span>
            <div class="chips">
              <span v-for="scan in selectedScans" :key="scan.id" class="chip">
                <span class="chip__label">{{ scan.name }}</span>
                <button type="button" class="chip__close" :aria-label="`Remove ${scan.name}`" @click="toggleScan(scan.id)"><v-icon :size="12" class="chip__close-icon">mdi-close</v-icon></button>
              </span>
            </div>
            <p class="preview"><v-icon :size="16" class="preview__icon">mdi-information-outline</v-icon><span>Creates <strong>1 analysis job</strong> for this scan.</span></p>
          </div>
        </div>

        <!-- AVA Inspect path -->
        <div v-else class="panel">
          <div v-if="!avaInspects.length" class="empty-state">
            <v-icon :size="32" class="empty-state__icon">mdi-vector-square</v-icon>
            <span class="empty-state__title">{{ copy.EMPTY_TITLE }}</span>
            <span class="empty-state__body">{{ copy.EMPTY_BODY }}</span>
            <span class="empty-state__hint">{{ copy.EMPTY_HINT }}</span>
          </div>
          <template v-else>
            <div class="field">
              <span class="field-label" id="runInspectLabel">AVA Inspect</span>
              <label class="tf"><v-icon :size="18" class="tf__icon">mdi-magnify</v-icon><input v-model="inspectQuery" type="text" placeholder="Search by title or master tag…" aria-label="Search AVA Inspect tags" /></label>
              <div class="list" role="radiogroup" aria-labelledby="runInspectLabel">
                <button v-for="tag in visibleInspects" :key="tag.id" type="button" class="list-row" role="radio" :aria-checked="String(avaInspectId === tag.id)" :aria-disabled="String(!tag.snapCount)" @click="pickInspect(tag)">
                  <span class="radio-dot"></span>
                  <span class="list-main">
                    <span class="list-name">{{ tag.title }}</span>
                    <span class="list-meta">
                      <span v-if="tag.masterTagName" class="chip chip--flat">{{ tag.masterTagName }}</span>
                      <span v-if="snapsLoading && !tag.snapCount" class="chip chip--flat">{{ copy.SNAPS_LOADING }}</span>
                      <span v-else-if="tag.snapCount" class="chip chip--snaps">{{ tag.snapCount }} {{ tag.snapCount === 1 ? 'snap' : 'snaps' }} on {{ tag.scanCount }} {{ tag.scanCount === 1 ? 'scan' : 'scans' }}</span>
                      <span v-else class="chip chip--warn">{{ copy.NO_SNAPS_HINT }}</span>
                      <span v-if="tag.retakeCount" class="chip chip--warn">{{ retakeText(tag.retakeCount) }}</span>
                    </span>
                  </span>
                </button>
                <p v-if="!visibleInspects.length" class="list-empty">No AVA Inspect matches “{{ inspectQuery }}”.</p>
              </div>
            </div>
            <p class="helper">{{ copy.TOGETHER }}</p>
            <p v-if="selectedInspect && selectedInspect.snapCount" class="preview">
              <v-icon :size="16" class="preview__icon">mdi-information-outline</v-icon>
              <span>Creates <strong>1 analysis job</strong>: {{ selectedInspect.snapCount }} {{ selectedInspect.snapCount === 1 ? 'snap' : 'snaps' }}, analysed in {{ roundCount }} {{ roundCount === 1 ? 'round' : 'rounds' }} of up to {{ snapsPerRound }}.</span>
            </p>
          </template>
        </div>
      </div>

      <footer class="run-dialog__foot">
        <span class="foot-reason" :data-tone="blockedReason ? 'blocked' : 'quiet'">
          <template v-if="footReason"><v-icon :size="14" class="foot-reason__icon">{{ blockedReason ? 'mdi-alert' : 'mdi-information-outline' }}</v-icon><span>{{ footReason }}</span></template>
        </span>
        <v-btn text class="foot-btn" :disabled="busy" @click="close">Cancel</v-btn>
        <v-btn depressed color="primary" class="foot-btn" :disabled="!!footReason || busy" :loading="busy" :title="footReason || ''" @click="confirm">
          <v-icon left :size="16">mdi-play</v-icon>
          Run analysis
        </v-btn>
      </footer>
    </section>
  </v-dialog>
</template>

<script>
  export const RUN_MODE = Object.freeze({ FULL_SCAN: 'FullScan', AVA_INSPECT: 'AvaInspect' });
  const DIALOG_WIDTH = 560;
  /** Snaps per model request on the API (OpenAIVisionOptions:MultiView:AvaInspectSnapsPerRound). */
  export const SNAPS_PER_ROUND = 4;
  const MODES = Object.freeze([
    { value: RUN_MODE.FULL_SCAN, label: 'Full Scan', icon: 'mdi-earth' },
    { value: RUN_MODE.AVA_INSPECT, label: 'AVA Inspect', icon: 'mdi-vector-square' },
  ]);
  // Copy mirrors the approved showcase; the processing sentence equals the top-bar tooltip so the surfaces agree.
  export const RUN_DIALOG_COPY = Object.freeze({
    SEG_HELPER_FULL_SCAN: 'Every selected scan is analysed end to end.',
    SEG_HELPER_AVA_INSPECT: 'Only the snap regions attached to the selected asset are analysed.',
    TOGETHER: 'All snaps of this tag are analysed together, so a defect seen from several scans is reported once.',
    SNAPS_LOADING: 'Loading snaps…',
    PROCESSING_TITLE: 'An analysis is already running for this facility.',
    PROCESSING_BODY: 'Wait for it to finish before starting another.',
    ERROR_TITLE: 'Could not start the analysis',
    EMPTY_TITLE: 'No AVA Inspect tags are visible here',
    EMPTY_BODY: 'AVA Inspect tags are the assets the AI groups its findings under. None exist for this facility yet, or none are visible to you — check that you have the AVA Inspect view permission.',
    EMPTY_HINT: 'Full Scan still works: switch back and pick a scan.',
    NO_SNAPS_HINT: 'No snaps yet — draw one on the Main View',
    REASON_NO_SCAN: 'Select a scan to run.',
    REASON_NO_INSPECT: 'Select an AVA Inspect to run.',
    REASON_NO_INSPECT_AVAILABLE: 'There is no AVA Inspect to run in this facility.',
    REASON_RETAKE: 'Retake the snaps saved before image outlines existed, then run this tag.',
    REASON_PROCESSING: 'Disabled while another analysis of this facility is processing.',
  });

  export default {
    name: 'RunAnalysisDialog',
    props: {
      value: { type: Boolean, default: false },
      facilityName: { type: String, default: '' },
      /** RUN_BLOCKED_REASON: non-null while the facility is processing; the body dims and Run is disabled. */
      blockedReason: { type: String, default: null },
      /** [{ id, name, captured?, note? }] — the facility's 360 scans. */
      scans: { type: Array, default: () => [] },
      /** [{ id, title, masterTagName, snapCount, scanCount, retakeCount }] — AVA_INSPECT_OPTIONS enriched with snap counts. */
      avaInspects: { type: Array, default: () => [] },
      /** Snap lists are still loading: tags without a count yet say so instead of "no snaps". */
      snapsLoading: { type: Boolean, default: false },
      /** Preselected scan id (scan-row trigger, ticket 10); only the first is used. */
      initialScanIds: { type: Array, default: () => [] },
      busy: { type: Boolean, default: false },
      /** Message of a failed start; the form keeps the selection so a retry costs nothing. */
      error: { type: String, default: null },
    },
    data() {
      return {
        width: DIALOG_WIDTH,
        modes: MODES,
        copy: RUN_DIALOG_COPY,
        mode: RUN_MODE.FULL_SCAN,
        selectedScanIds: [],
        avaInspectId: null,
        snapsPerRound: SNAPS_PER_ROUND,
        scanQuery: '',
        inspectQuery: '',
      };
    },
    computed: {
      isFullScan() {
        return this.mode === RUN_MODE.FULL_SCAN;
      },
      visibleScans() {
        const q = this.scanQuery.trim().toLowerCase();
        return this.scans.filter((s) => !q || String(s.name || '').toLowerCase().includes(q));
      },
      selectedScans() {
        return this.scans.filter((s) => this.selectedScanIds.includes(s.id));
      },
      visibleInspects() {
        const q = this.inspectQuery.trim().toLowerCase();
        return this.avaInspects.filter((t) => !q || String(t.title || '').toLowerCase().includes(q) || String(t.masterTagName || '').toLowerCase().includes(q));
      },
      selectedInspect() {
        return this.avaInspects.find((t) => t.id === this.avaInspectId) || null;
      },
      roundCount() {
        return Math.ceil(((this.selectedInspect && this.selectedInspect.snapCount) || 0) / SNAPS_PER_ROUND);
      },
      /** The reason Run is disabled, or '' when the form can be submitted (same order as the showcase). */
      footReason() {
        if (this.blockedReason) return RUN_DIALOG_COPY.REASON_PROCESSING;
        if (this.isFullScan) return this.selectedScanIds.length ? '' : RUN_DIALOG_COPY.REASON_NO_SCAN;
        if (!this.avaInspects.some((t) => t.snapCount > 0)) return RUN_DIALOG_COPY.REASON_NO_INSPECT_AVAILABLE;
        if (!this.selectedInspect || !(this.selectedInspect.snapCount > 0)) return RUN_DIALOG_COPY.REASON_NO_INSPECT;
        return this.selectedInspect.retakeCount ? RUN_DIALOG_COPY.REASON_RETAKE : '';
      },
    },
    watch: {
      value(open) {
        if (open) this.reset();
      },
    },
    methods: {
      reset() {
        this.mode = RUN_MODE.FULL_SCAN;
        this.selectedScanIds = this.initialScanIds.slice(0, 1);
        this.avaInspectId = null;
        this.scanQuery = '';
        this.inspectQuery = '';
      },
      // Full Scan takes exactly one scan (Khalid, 2026-09-07): picking another replaces it, picking it again clears it.
      toggleScan(id) {
        this.selectedScanIds = this.selectedScanIds[0] === id ? [] : [id];
      },
      pickInspect(tag) {
        if (!tag.snapCount) return;
        this.avaInspectId = this.avaInspectId === tag.id ? null : tag.id;
      },
      close() {
        this.$emit('input', false);
      },
      retakeText(count) {
        return `${count} ${count === 1 ? 'snap needs' : 'snaps need'} retaking`;
      },
      confirm() {
        if (this.footReason || this.busy) return;
        this.$emit('run', this.isFullScan
          ? { mode: RUN_MODE.FULL_SCAN, imagesIds: [...this.selectedScanIds] }
          : { mode: RUN_MODE.AVA_INSPECT, avaInspectId: this.avaInspectId });
      },
    },
  };
</script>

<style lang="scss">
  @import './aiAnalysisTokens';

  // Unscoped: v-dialog teleports the content to the app root, so the tokens are included on the dialog root itself.
  .run-dialog {
    @include ai-analysis-tokens;
    display: flex;
    flex-direction: column;
    background: var(--aa-surface);
    color: var(--aa-text-primary);
    border-radius: var(--ds-radius-sm);
    font-family: var(--ds-font-family-body);

    &__head { display: flex; align-items: flex-start; gap: var(--ds-space-md); padding: var(--ds-space-md) var(--ds-space-md) var(--ds-space-sm); border-bottom: 1px solid var(--aa-border-subtle); }
    &__head-text { margin-right: auto; }
    &__title { margin: 0; font-family: var(--ds-font-family-heading) !important; font-size: var(--ds-font-title); font-weight: 600; }
    &__sub { margin: 2px 0 0; font-size: var(--ds-font-caption); color: var(--aa-text-muted); }
    &__close { color: var(--aa-text-muted) !important; }
    &__alerts { padding: var(--ds-space-md) var(--ds-space-md) 0; }
    &__body { display: flex; flex-direction: column; gap: var(--ds-space-md); padding: var(--ds-space-md); max-height: 70vh; overflow: auto; }
    &.is-processing &__body { opacity: 0.5; pointer-events: none; filter: grayscale(0.35); }
    &__foot { display: flex; align-items: center; gap: var(--ds-space-md); padding: var(--ds-space-md); border-top: 1px solid var(--aa-border-subtle); background: var(--aa-surface-subtle); border-radius: 0 0 var(--ds-radius-sm) var(--ds-radius-sm); }

    .alert { display: flex; gap: var(--ds-space-sm); align-items: flex-start; padding: 10px var(--ds-space-md); border-radius: var(--ds-radius-sm); border-left: 3px solid transparent; font-size: var(--ds-font-body-sm); line-height: 1.5; }
    .alert strong { display: block; font-weight: 600; }
    .alert__body { flex: 1; }
    .alert__icon { color: inherit !important; margin-top: 1px; }
    .alert--warn { background: rgba(210, 160, 0, 0.1); border-left-color: #d2a000; color: #6b5200; }
    .alert--error { background: rgba(183, 28, 28, 0.08); border-left-color: var(--aa-error); color: #7f1d1d; }
    .alert__action { text-transform: none; letter-spacing: 0; }

    .field { display: flex; flex-direction: column; gap: var(--ds-space-sm); }
    .field-label { font-size: var(--ds-font-caption); font-weight: 600; letter-spacing: 0.04em; text-transform: uppercase; color: var(--aa-text-muted); }
    .helper { margin: calc(var(--ds-space-xs) * -1) 0 0; font-size: var(--ds-font-caption); color: var(--aa-text-muted); line-height: 1.5; }
    .panel { display: flex; flex-direction: column; gap: var(--ds-space-md); }

    /* Mode choice — variant A, segmented control */
    .seg { display: flex; border: 1px solid var(--aa-border-default); border-radius: var(--ds-radius-sm); overflow: hidden; }
    .seg-btn { flex: 1; display: flex; align-items: center; justify-content: center; gap: var(--ds-space-sm); padding: 10px var(--ds-space-md); border: 0; background: var(--aa-surface); font: 500 var(--ds-font-body) / 1.2 var(--ds-font-family-body); color: var(--aa-text-secondary); cursor: pointer; transition: background var(--ds-transition-fast), color var(--ds-transition-fast); }
    .seg-btn + .seg-btn { border-left: 1px solid var(--aa-border-default); }
    .seg-btn:hover { background: var(--aa-surface-hover); }
    .seg-btn[aria-checked='true'] { background: var(--aa-brand); color: #fff; }
    .seg-btn__icon { color: inherit !important; }
    .seg-btn:focus-visible { outline: 2px solid var(--aa-focus); outline-offset: -2px; }

    .tf { display: flex; align-items: center; gap: var(--ds-space-sm); height: 40px; padding: 0 var(--ds-space-sm); border: 1px solid var(--aa-border-default); border-radius: var(--ds-radius-sm); background: var(--aa-surface); transition: border-color var(--ds-transition-fast), box-shadow var(--ds-transition-fast); }
    .tf:focus-within { border-color: var(--aa-brand); box-shadow: 0 0 0 1px var(--aa-brand); }
    .tf__icon { color: var(--aa-text-muted) !important; }
    .tf input { flex: 1; border: 0; outline: 0; background: transparent; font-size: var(--ds-font-body); color: var(--aa-text-primary); }
    .tf input::placeholder { color: var(--aa-text-disabled); }

    .list { border: 1px solid var(--aa-border-subtle); border-radius: var(--ds-radius-sm); max-height: 216px; overflow: auto; background: var(--aa-surface); }
    .list-row { display: flex; align-items: center; gap: var(--ds-space-sm); width: 100%; padding: 10px var(--ds-space-md); border: 0; border-bottom: 1px solid var(--aa-border-subtle); background: transparent; text-align: left; font-family: var(--ds-font-family-body); cursor: pointer; transition: background var(--ds-transition-fast); }
    .list-row:last-of-type { border-bottom: 0; }
    .list-row:hover { background: var(--aa-surface-hover); }
    .list-row[aria-checked='true'] { background: var(--aa-surface-active); }
    .list-row[aria-disabled='true'] { cursor: not-allowed; opacity: 0.62; }
    .list-row[aria-disabled='true']:hover { background: transparent; }
    .list-row:focus-visible { outline: 2px solid var(--aa-focus); outline-offset: -2px; }
    .list-main { flex: 1; min-width: 0; }
    .list-name { display: block; font-size: var(--ds-font-body); font-weight: 500; color: var(--aa-text-primary); }
    .list-meta { display: flex; flex-wrap: wrap; gap: var(--ds-space-xs) var(--ds-space-sm); align-items: center; margin-top: 2px; font-size: var(--ds-font-caption); color: var(--aa-text-muted); }
    .list-empty { margin: 0; padding: var(--ds-space-md); font-size: var(--ds-font-body-sm); color: var(--aa-text-muted); text-align: center; }
    .box { display: grid; place-items: center; flex: none; width: 18px; height: 18px; border: 2px solid var(--aa-border-strong); border-radius: 3px; color: #fff; }
    .box__icon { color: inherit !important; display: none; }
    .list-row[aria-checked='true'] .box { background: var(--aa-brand); border-color: var(--aa-brand); }
    .list-row[aria-checked='true'] .box__icon { display: inline-flex; }
    .radio-dot { display: grid; place-items: center; flex: none; width: 16px; height: 16px; border: 2px solid var(--aa-border-strong); border-radius: var(--ds-radius-full); }
    .list-row[role='radio'][aria-checked='true'] .radio-dot { border-color: var(--aa-brand); }
    .list-row[role='radio'][aria-checked='true'] .radio-dot::after { content: ''; width: 8px; height: 8px; border-radius: var(--ds-radius-full); background: var(--aa-brand); }

    .chips { display: flex; flex-wrap: wrap; gap: var(--ds-space-xs); }
    .chip { display: inline-flex; align-items: center; gap: var(--ds-space-xs); max-width: 100%; padding: 3px 6px 3px 10px; border-radius: var(--ds-radius-full); background: rgba(3, 98, 76, 0.1); color: var(--aa-brand); font-size: var(--ds-font-caption); font-weight: 500; }
    .chip__label { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .chip__close { display: grid; place-items: center; flex: none; width: 16px; height: 16px; border: 0; border-radius: var(--ds-radius-full); background: rgba(3, 98, 76, 0.16); color: var(--aa-brand); cursor: pointer; }
    .chip__close:hover { background: var(--aa-brand); color: #fff; }
    .chip__close-icon { color: inherit !important; }
    .chip--flat { padding: 2px 8px; background: var(--aa-surface-hover); color: var(--aa-text-secondary); font-family: var(--ds-font-family-mono) !important; font-size: var(--aa-font-micro); letter-spacing: 0.02em; }
    .chip--snaps { padding: 2px 8px; background: rgba(84, 125, 154, 0.14); color: #2b4a5f; }
    .chip--warn { padding: 2px 8px; background: rgba(210, 160, 0, 0.16); color: #6b5200; }


    .preview { display: flex; align-items: center; gap: var(--ds-space-sm); margin: 0; font-size: var(--ds-font-body-sm); font-weight: 500; color: var(--ds-brand-castleton-dark); }
    .preview__icon { color: var(--aa-brand) !important; }

    .empty-state { display: flex; flex-direction: column; align-items: center; gap: var(--ds-space-sm); padding: var(--ds-space-xl) var(--ds-space-lg); border: 1px dashed var(--aa-border-strong); border-radius: var(--ds-radius-sm); background: var(--aa-surface-subtle); text-align: center; }
    .empty-state__icon { color: var(--aa-text-disabled) !important; }
    .empty-state__title { font-family: var(--ds-font-family-heading) !important; font-size: var(--ds-font-subtitle); font-weight: 600; }
    .empty-state__body { max-width: 44ch; font-size: var(--ds-font-body-sm); color: var(--aa-text-secondary); line-height: 1.6; }
    .empty-state__hint { font-size: var(--ds-font-caption); color: var(--aa-text-muted); }

    .foot-reason { flex: 1; min-width: 0; display: flex; gap: var(--ds-space-xs); align-items: center; font-size: var(--ds-font-caption); color: var(--aa-text-muted); line-height: 1.4; }
    .foot-reason[data-tone='blocked'] { color: #6b5200; }
    .foot-reason__icon { color: inherit !important; }
    .foot-btn { letter-spacing: 0.02em; }
  }

  .theme--dark .run-dialog {
    @include ai-analysis-tokens-dark;
  }
</style>
